import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import {
  downloadQRPNG,
  getQRCodeDataUrl,
} from '../../../shared/util/getQRCode';
import { getConfig } from '../../../shared/env/envManager';
import { useSetting } from '../../../shared/hooks/useApi/useSetting';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import belowIcon from '../../../asset/icon/below.png';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';
import { MobileHeader, Title } from '../../../shared/component/modal/SearchModalLayout';
import QRGeneratorPreview from '../QRGeneratorPreview';

const QRGeneratorModalMobile: React.FC = () => {
  const { clearModal } = useModalStore();
  const { setting } = useSetting();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const pageOwnerAui = pageOwner?.aui;

  const [isTransparent, setIsTransparent] = useState<boolean>(true);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const qrUrl = useMemo(() => {
    if (!pageOwnerAui) return null;
    const config = getConfig();
    return `https://${config.appDomain}/${pageOwnerAui}`;
  }, [pageOwnerAui]);

  useEffect(() => {
    if (!qrUrl) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    void getQRCodeDataUrl(qrUrl, isTransparent).then((url) => {
      if (cancelled) return;
      setPreviewDataUrl(url);
      setPreviewLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [qrUrl, isTransparent]);

  if (!pageOwner || !setting) return null;

  const handleGenerate = () => {
    if (!qrUrl) return;
    downloadQRPNG(qrUrl, `QR-${setting.username}.png`, isTransparent);
    clearModal(400);
  };

  const handleClose = () => {
    clearModal(400);
  };

  return (
    <QRCodeGeneratorComp>
      <MobileHeader>
        <Title>{getModalMessage('QR_CODE_OPTIONS_TITLE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </MobileHeader>
      <QRPreviewWrapper>
        <QRGeneratorPreview
          dataUrl={previewDataUrl}
          isTransparent={isTransparent}
          loading={previewLoading}
        />
      </QRPreviewWrapper>
      <OptionContainer>
        <OptionSection>
          <OptionLabel>{getModalMessage('BACKGROUND')}</OptionLabel>
          <RadioGroup>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="background"
                checked={isTransparent === true}
                onChange={() => setIsTransparent(true)}
              />
              {getModalMessage('TRANSPARENT')}
            </CheckboxComp>
            <CheckboxComp>
              <Checkbox
                type="radio"
                name="background"
                checked={isTransparent === false}
                onChange={() => setIsTransparent(false)}
              />
              {getModalMessage('WHITE')}
            </CheckboxComp>
          </RadioGroup>
        </OptionSection>
      </OptionContainer>
      <HeadlessBtn
        value={getBtnName('GENERATE')}
        handleClick={handleGenerate}
        StyledBtn={SubmitButton}
      />
    </QRCodeGeneratorComp>
  );
};

const QRCodeGeneratorComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  max-height: 95vh;
  gap: clamp(12px, 3vh, 20px);
`;

const QRPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OptionSection = styled.div`
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionLabel = styled.div`
  min-width: 70px;
  flex-shrink: 0;
  padding: 2px 0;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Checkbox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;


const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default QRGeneratorModalMobile;
